// TICKET-4 🔴 Fonctions non testées : computeDevisTotalTVA, computeDevisTotalTTC, readDevisStore, saveDevisStore
// Voir devis.test.ts pour les tests existants

import { UserProfile } from '@shared/types';

export const DEVIS_STORAGE_KEY = 'indxone.devis.v1';

export type DevisStatus = 'brouillon' | 'envoye' | 'accepte' | 'encaisse' | 'refuse';

export interface DevisLine {
  desc: string;
  qty: number;
  pu: number;
  tva?: number;
}

export interface DevisRecord {
  id: string;
  num: string;
  status: DevisStatus;
  date: string;
  validUntil: string;
  senderName: string;
  senderSiret: string;
  senderEmail: string;
  senderTel: string;
  senderAddr: string;
  clientName: string;
  clientContact: string;
  clientEmail: string;
  clientAddr: string;
  object: string;
  notes: string;
  cgv: string;
  totalHT: number;
  tva: number;
  lines: DevisLine[];
  projectId?: string;
  missionId?: string;
  createdAt: string;
  updatedAt: string;
}

export const DEFAULT_CGV = `Paiement : à 30 jours nets de réception de facture.
Acompte : un acompte de 30% peut être demandé à la commande.
Rétention de propriété : les prestations restent la propriété du prestataire jusqu'au paiement intégral.
Litige : en cas de litige, le tribunal compétent est celui du siège du prestataire.
Droit applicable : droit français.
Protection des données : les informations échangées sont confidentielles et ne seront pas divulguées à des tiers sans accord préalable.`;

function uid() {
  return `devis_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function validUntilIso() {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  return date.toISOString().slice(0, 10);
}

export function nextDevisNumber(existing: DevisRecord[]) {
  const year = new Date().getFullYear();
  const numbers = existing
    .map((devis) => devis.num || '')
    .filter((num) => num.startsWith(`D-${year}-`));
  const max = numbers.reduce((highest, num) => Math.max(highest, parseInt(num.split('-')[2] || '0', 10)), 0);
  return `D-${year}-${String(max + 1).padStart(3, '0')}`;
}

export function readDevisStore(): DevisRecord[] {
  try {
    const raw = localStorage.getItem(DEVIS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { devis?: DevisRecord[] } | null;
    return Array.isArray(parsed?.devis) ? parsed.devis : [];
  } catch {
    return [];
  }
}

export function saveDevisStore(records: DevisRecord[]) {
  localStorage.setItem(DEVIS_STORAGE_KEY, JSON.stringify({ devis: records }));
}

export function computeDevisTotalHT(lines: DevisLine[]) {
  return lines.reduce((sum, line) => sum + (Number(line.qty) || 0) * (Number(line.pu) || 0), 0);
}

export function computeDevisTotalTVA(lines: DevisLine[], defaultTva: number) {
  return lines.reduce((sum, line) => {
    const ht = (Number(line.qty) || 0) * (Number(line.pu) || 0);
    const rate = line.tva ?? defaultTva;
    return sum + ht * rate / 100;
  }, 0);
}

export function computeDevisTotalTTC(lines: DevisLine[], defaultTva: number) {
  return computeDevisTotalHT(lines) + computeDevisTotalTVA(lines, defaultTva);
}

export function buildProfileAddress(profile: UserProfile) {
  return [profile.address, [profile.postalCode, profile.city].filter(Boolean).join(' '), profile.country].filter(Boolean).join(', ');
}

export function createDevisDraft(input: {
  profile: UserProfile;
  project?: {
    id?: string;
    client?: string;
    name?: string;
    owner?: string;
    tjm?: number;
  } | null;
}) {
  const existing = readDevisStore();
  const now = new Date().toISOString();
  const lines: DevisLine[] = [
    {
      desc: input.project?.name || 'Prestation',
      qty: 1,
      pu: input.project?.tjm || input.profile.tjm || 0,
    },
  ];

  const draft: DevisRecord = {
    id: uid(),
    num: nextDevisNumber(existing),
    status: 'brouillon',
    date: todayIso(),
    validUntil: validUntilIso(),
    senderName: `${input.profile.firstName} ${input.profile.lastName}`.trim(),
    senderSiret: input.profile.siret || '',
    senderEmail: input.profile.email || '',
    senderTel: input.profile.phone || '',
    senderAddr: buildProfileAddress(input.profile),
    clientName: input.project?.client || '',
    clientContact: input.project?.owner || '',
    clientEmail: '',
    clientAddr: '',
    object: input.project?.name || '',
    notes: 'Paiement a 30 jours. Acompte possible a la commande.',
    cgv: DEFAULT_CGV,
    totalHT: computeDevisTotalHT(lines),
    tva: input.profile.status === 'ei' ? 0 : 20,
    lines,
    projectId: input.project?.id,
    missionId: input.project?.id,
    createdAt: now,
    updatedAt: now,
  };

  return draft;
}

export function upsertDevis(record: DevisRecord) {
  const existing = readDevisStore();
  const next = [record, ...existing.filter((item) => item.id !== record.id)];
  saveDevisStore(next);
}

export function duplicateDevis(record: DevisRecord) {
  const duplicate: DevisRecord = {
    ...record,
    id: uid(),
    num: nextDevisNumber(readDevisStore()),
    status: 'brouillon',
    date: todayIso(),
    validUntil: validUntilIso(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  upsertDevis(duplicate);
  return duplicate;
}
